/**
 * Stitch-connection spec builders (#984) — the ONE source of truth for how a
 * stitch-match (#221) or construction-stitch-grid (#199 Phase 3) relationship
 * describes itself, shared by both surfaces that render it:
 *
 *   - the CHIP in the parts / layers panel  (`src/ui/LayersPanel.ts`)
 *   - the ROW in Properties → Connections   (`src/ui/stitchPanelSection.ts`)
 *
 * `ConnectionRowSpec extends ConnectionChipSpec`, so a row IS a chip plus a
 * `label` (and, for a detachable match, `onDetach`). Both surfaces therefore
 * build from the same base here and physically cannot disagree about the
 * partner link (`clickTarget`), the cross-highlight `connectionKey`, the role,
 * the icon, or the CSS class.
 *
 * They used to be four independent functions in two UI files, which is exactly
 * how #979 happened: the chips carried `clickTarget` (so clicking selected the
 * partner) while the rows did not (so clicking them did nothing). Keeping the
 * shared identity in one place removes the room for that drift. Mirrors how the
 * trace connection already emits both its part-side row and chip from
 * `traceConnection.ts`.
 *
 * Labels stay per-surface where they genuinely differ: a grid LEADER shows one
 * summary chip ("Grid leader · 3 seams align to this") but one row per follower
 * ("Aligns Stitch 2 to this grid"), so the caller passes the text and this
 * module guarantees everything else matches.
 */
import stitchingSvg from "../../icons/stitching.svg?raw";
import { stripIconSvg } from "../../icons/stripIconSvg";
import type { ConnectionChipSpec } from "./Connection";

/** Addresses one seam on its owning part — the shape both surfaces use for
 *  partner links. */
export interface StitchSpecRef {
  pieceId: string;
  stitchId: string;
}

/** Stitching glyph (currentColor) shared by every stitch-connection chip / row.
 *  Kept here beside the specs so the two surfaces can't pick different icons;
 *  `layersRowKit` re-exports it as `STITCH_MATCH_CHIP_ICON` for the row kit. */
export const STITCH_CONNECTION_ICON = stripIconSvg(stitchingSvg);

/** Cross-highlight key for a stitch match — every chip + row of the SAME match
 *  shares it, keyed on the match record. */
export const stitchMatchKey = (matchId: string): string =>
  `stitchmatch:${matchId}`;

/** Cross-highlight key for a grid group — keyed on the LEADER so the leader's
 *  chip, each of its rows, and every follower's chip / row all share one key. */
export const stitchGridKey = (leaderStitchId: string): string =>
  `stitchgrid:${leaderStitchId}`;

/** Match wording, one definition for both surfaces: the follower "Matches to"
 *  its main; the main is "Matched by" its follower. */
export const stitchMatchLabel = (
  partnerName: string,
  isMain: boolean,
): string =>
  isMain ? `Matched by ${partnerName}` : `Matches to ${partnerName}`;

/** Shared identity of one side of a stitch MATCH. The chip renders this as-is;
 *  the row spreads it and adds `label` + the detach commit. */
export function stitchMatchSpecBase(
  matchId: string,
  partner: StitchSpecRef,
  partnerName: string,
  isMain: boolean,
): ConnectionChipSpec {
  const label = stitchMatchLabel(partnerName, isMain);
  return {
    kind: "stitchMatch",
    connectionKey: stitchMatchKey(matchId),
    role: isMain ? "main" : "follower",
    iconSvg: STITCH_CONNECTION_ICON,
    ariaLabel: label,
    title: label,
    className: "stitch-match-chip",
    hoverRefs: [],
    // Click jumps to the matched seam on its own part (#221).
    clickTarget: { pieceId: partner.pieceId, stitchId: partner.stitchId },
  };
}

/** Shared identity of one side of a construction-stitch GRID. `label` is passed
 *  in because the two surfaces word it differently (see the module docstring);
 *  everything that must match is fixed here. `partner` is the seam a click
 *  jumps to — a follower's leader, or (for the leader) the aligned seam this
 *  chip / row stands for. Null when the group has no resolvable partner, which
 *  leaves the click a no-op rather than selecting the wrong seam. */
export function stitchGridSpecBase(
  leaderStitchId: string,
  partner: StitchSpecRef | null,
  isLeader: boolean,
  label: string,
): ConnectionChipSpec {
  return {
    kind: "stitchGrid",
    connectionKey: stitchGridKey(leaderStitchId),
    role: isLeader ? "main" : "follower",
    iconSvg: STITCH_CONNECTION_ICON,
    ariaLabel: label,
    title: label,
    className: "stitch-grid-chip",
    hoverRefs: [],
    // Click jumps to the grid partner (#221): a follower selects the leader; a
    // leader selects the seam aligned to it that this chip / row represents.
    ...(partner
      ? {
          clickTarget: { pieceId: partner.pieceId, stitchId: partner.stitchId },
        }
      : {}),
  };
}

/** Which seam the LEADER's single summary chip jumps to: the first member that
 *  isn't the leader itself. Lives here so the chip's choice sits beside the
 *  rows' per-member choice instead of being re-derived in the layers panel. */
export function stitchGridChipPartner(
  members: ReadonlyArray<StitchSpecRef>,
  selfStitchId: string,
): StitchSpecRef | null {
  return members.find((m) => m.stitchId !== selfStitchId) ?? null;
}
